/**
 * CR-7274 rework: the scope the Performance tab is configuring, for every
 * editor on it.
 *
 * The "Configure settings for" picker names a site, a Group Default or the
 * platform. The settings form already took that scope as a hook argument,
 * but the Grade Colours panel, the three band editors, the score-mapping
 * resets and the KPI target ranges each called their own endpoint with no
 * scope at all — so the server fell back to the viewer's home site. Testing
 * on 20 Oct 2026 found the palette showing the home site's colours under
 * every selection; a save there would have recoloured the wrong site.
 *
 * One provider around the tab, read by each hook, so no editor can be
 * forgotten the way a prop drilled through three layers can.
 */
import { createContext, useContext, ReactNode } from 'react';

import type { ScopeSelection } from '../../../lib/api/performanceSettings';

const SettingsScopeContext = createContext<ScopeSelection | undefined>(undefined);

interface SettingsScopeProviderProps {
  /** The picker's selection; `undefined` is the caller's own context, as before. */
  scope: ScopeSelection | undefined;
  children: ReactNode;
}

/**
 * Provide the configured scope to every performance-settings editor beneath.
 *
 * @param scope - the picker's selection, or `undefined` for the default context
 */
export function SettingsScopeProvider({ scope, children }: SettingsScopeProviderProps) {
  return <SettingsScopeContext.Provider value={scope}>{children}</SettingsScopeContext.Provider>;
}

/**
 * The scope the Performance tab is configuring.
 *
 * Deliberately does not throw outside a provider: an editor rendered on its
 * own (a test, or a future page without the picker) simply uses the caller's
 * default context, which is exactly what every endpoint does with no scope.
 *
 * @returns the configured scope, or `undefined` for the default context
 */
export function useSettingsScope(): ScopeSelection | undefined {
  return useContext(SettingsScopeContext);
}
